import { FC, PropsWithChildren, useState } from 'react'
import {
  Text,
  CopyButton,
  FontFamiliesInconsolata,
  GreyGrey30,
  GreyGrey70,
  GreyGrey80,
  IconSequence,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary20SemiBold,
  SettingsFormControl,
  SettingsGroup,
  Switch,
  Tooltip,
  Flex
} from '@sequencehq/core-components'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { SettingsPageLayout } from '../components/SettingsPageLayout'
import { AsciiShift } from '../components/labs/AsciiShift'
import { LabsActivationCard } from '../components/labs/LabsActivationCard'
import { useLabsSettings } from '../components/labs/useLabsSettings'
import { CUSTOMER_QUERIES_AGENT_DOCS_URL, MCP_DOCS_URL } from './labs.constants'

export const LabsSettingsPage: FC = () => {
  const { showCustomerQueriesAgent } = useFeatureFlags()
  const {
    isLabsEnabled,
    isMcpEnabled,
    mcpServerName,
    mcpServerUrl,
    isEmailAgentEnabled,
    emailAgentAddress,
    isLoading,
    isError,
    isPending,
    canUpdate,
    toggleLabs,
    toggleMcp,
    toggleEmailAgent
  } = useLabsSettings()

  // Bumped each time Labs is enabled to remount (and replay) the activation
  // effect: the shock-wave ring (overlay) and the border beam (card wrapper).
  const [pulseKey, setPulseKey] = useState(0)
  const replayActivation = () => setPulseKey(key => key + 1)

  const handleToggleLabs = (enabled: boolean) => {
    if (enabled) replayActivation()
    toggleLabs(enabled)
  }

  return (
    <SettingsPageLayout
      header="Labs"
      headerContent={<LabsTitle />}
      subheader="Experimental features and tools."
      isError={isError}
    >
      <LabsActivationCard
        title="Sequence Labs"
        footer="Opt in to try out experimental features before they're generally available."
        isLoading={isLoading}
        pulseKey={pulseKey}
      >
        <SettingsFormControl
          label="Enable Sequence Labs"
          helper="Turn on early-access features for your account."
          htmlFor="enable-sequence-labs"
        >
          <CanUpdateTooltip canUpdate={canUpdate}>
            <Switch
              size="md"
              id="enable-sequence-labs"
              data-testid="labs.enableLabs"
              disabled={isPending || !canUpdate}
              checked={isLabsEnabled}
              onChange={handleToggleLabs}
            />
          </CanUpdateTooltip>
        </SettingsFormControl>
      </LabsActivationCard>

      {isLabsEnabled && (
        <>
          <SettingsGroup title="MCP server">
            <SettingsFormControl
              label="Enable MCP"
              helper={
                <>
                  Connect external AI tools via MCP.{' '}
                  <Link
                    to={MCP_DOCS_URL}
                    isExternal
                    data-testid="labs.docsLink"
                    color={IndigoIndigo60}
                    _hover={{ textDecoration: 'none' }}
                  >
                    View docs
                  </Link>
                </>
              }
              htmlFor="enable-mcp"
            >
              <CanUpdateTooltip canUpdate={canUpdate}>
                <Switch
                  size="md"
                  id="enable-mcp"
                  data-testid="labs.enableMcp"
                  disabled={isPending || !canUpdate}
                  checked={isMcpEnabled}
                  onChange={toggleMcp}
                />
              </CanUpdateTooltip>
            </SettingsFormControl>

            {isMcpEnabled && (
              <>
                <SettingDetailRow
                  label="Name"
                  value={mcpServerName}
                  valueTestId="labs.mcp.serverName"
                />
                <SettingDetailRow
                  label="MCP Server URL"
                  value={mcpServerUrl}
                  valueTestId="labs.mcp.serverUrl"
                  showBorder={false}
                />
              </>
            )}
          </SettingsGroup>

          {showCustomerQueriesAgent && (
            <SettingsGroup
              title="AR agent"
              footer={
                isEmailAgentEnabled
                  ? 'Only people registered as users in this workspace can email this agent.'
                  : undefined
              }
            >
              <SettingsFormControl
                label="Enable AR agent"
                helper={
                  <>
                    Allow Sequence agent to act on customer queries on your
                    behalf.{' '}
                    <Link
                      to={CUSTOMER_QUERIES_AGENT_DOCS_URL}
                      isExternal
                      data-testid="labs.ARAgent.docsLink"
                      color={IndigoIndigo60}
                      _hover={{ textDecoration: 'none' }}
                    >
                      View docs
                    </Link>
                  </>
                }
                htmlFor="enable-ar-agent"
              >
                <CanUpdateTooltip canUpdate={canUpdate}>
                  <Switch
                    size="md"
                    id="enable-ar-agent"
                    data-testid="labs.enableARAgent"
                    disabled={isPending || !canUpdate}
                    checked={isEmailAgentEnabled}
                    onChange={toggleEmailAgent}
                  />
                </CanUpdateTooltip>
              </SettingsFormControl>

              {isEmailAgentEnabled && emailAgentAddress && (
                <SettingDetailRow
                  label="Agent email"
                  value={emailAgentAddress}
                  valueTestId="labs.ARAgent.email"
                  showBorder={false}
                />
              )}
            </SettingsGroup>
          )}
        </>
      )}
    </SettingsPageLayout>
  )
}

const LabsTitle: FC = () => (
  <Flex alignItems="center" gap="8px" minH="24px">
    <IconSequence width="20px" />
    <AsciiShift
      text="/ Labs"
      dur={1000}
      spread={1}
      revealDelay={500}
      style={{
        color: GreyGrey80,
        ...Primary20SemiBold,
        fontFamily: FontFamiliesInconsolata
      }}
    />
  </Flex>
)

const CanUpdateTooltip: FC<PropsWithChildren<{ canUpdate: boolean }>> = ({
  canUpdate,
  children
}) => (
  <Tooltip
    isDisabled={canUpdate}
    placement="top"
    label="Only workspace admins can change this setting."
  >
    {children}
  </Tooltip>
)

const SettingDetailRow: FC<{
  label: string
  value: string
  valueTestId?: string
  showBorder?: boolean
}> = ({ label, value, valueTestId, showBorder = true }) => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    height="48px"
    borderBottom={showBorder ? `1px solid ${GreyGrey30}` : undefined}
  >
    <Text color={GreyGrey70} {...Primary13Regular}>
      {label}
    </Text>
    <Flex alignItems="center" gap="8px">
      <Text color={GreyGrey80} data-testid={valueTestId} {...Primary13Regular}>
        {value}
      </Text>
      <CopyButton value={value} enableGroupHover={false} />
    </Flex>
  </Flex>
)
